/**
 * ResolveClusterModal — the Doc 12 Rule 1 decision for one cluster.
 *
 * "Cluster review must not silently collapse items. Cluster resolution must
 * explicitly decide whether the right outcome is duplicate, variant, or
 * item-native reframe." Three outcomes, one required rationale, and a plain
 * sentence describing exactly what will happen before anything is written.
 * The plan is computed by src/lib/clusterResolution.ts; the store executes it.
 */
import { useEffect, useMemo, useState } from 'react';
import { GitMerge, Loader2 } from 'lucide-react';
import { Modal, Select, TextArea } from '../../ds';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import {
  OUTCOME_LABELS,
  defaultCanonicalItemId,
  planClusterResolution,
  type ClusterResolutionOutcome,
} from '../../../lib/clusterResolution';

interface ResolveClusterModalProps {
  open: boolean;
  onClose: () => void;
  groupId: string | null;
  /** Called after a successful resolution; reframe callers navigate to the kept item. */
  onResolved?: (result: { outcome: ClusterResolutionOutcome; canonicalItemId?: string }) => void;
}

const OUTCOMES: ClusterResolutionOutcome[] = ['duplicate', 'variant', 'reframe'];

export function ResolveClusterModal({ open, onClose, groupId, onResolved }: ResolveClusterModalProps) {
  const groups = useItemRelationshipsStore((s) => s.groups);
  const memberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const resolveCluster = useItemRelationshipsStore((s) => s.resolveCluster);
  const items = useItemsStore((s) => s.items);
  const userId = useUserStore((s) => s.userId);
  const addNotification = useUIStore((s) => s.addNotification);

  const group = useMemo(() => groups.find((g) => g.id === groupId), [groups, groupId]);
  const groupMembers = useMemo(() => memberships.filter((m) => m.groupId === groupId), [memberships, groupId]);
  const memberItems = useMemo(
    () => groupMembers.map((m) => items.find((i) => i.id === m.itemId)).filter((i): i is NonNullable<typeof i> => !!i),
    [groupMembers, items],
  );

  const [outcome, setOutcome] = useState<ClusterResolutionOutcome>('duplicate');
  const [canonicalItemId, setCanonicalItemId] = useState('');
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setOutcome((group?.subtype as ClusterResolutionOutcome) === 'variant' ? 'variant' : 'duplicate');
    setCanonicalItemId(defaultCanonicalItemId(groupMembers) ?? memberItems[0]?.id ?? '');
    setRationale('');
    setError('');
    setBusy(false);
    // Reset on open only; later store refreshes must not clobber the user's picks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, groupId]);

  const preview = useMemo(() => {
    if (!group) return null;
    const r = planClusterResolution({ group, memberships, outcome, canonicalItemId: canonicalItemId || undefined });
    return r;
  }, [group, memberships, outcome, canonicalItemId]);

  const titleOf = (id: string) => memberItems.find((i) => i.id === id)?.title ?? id;
  const needsCanonical = outcome !== 'variant';

  const handleConfirm = async () => {
    if (!group || !userId) return;
    if (!rationale.trim()) {
      setError('Say why — the rationale is what makes this defensible later.');
      return;
    }
    setBusy(true);
    setError('');
    const res = await resolveCluster(group.id, {
      outcome,
      canonicalItemId: needsCanonical ? canonicalItemId : undefined,
      rationale,
      actorId: userId,
    });
    setBusy(false);
    if (!res.success) {
      setError(res.error ?? 'Could not resolve the cluster.');
      return;
    }
    addNotification({
      type: 'success',
      title: 'Cluster resolved',
      message:
        outcome === 'variant'
          ? 'Kept as variants — both remain individually rankable.'
          : `Kept "${titleOf(canonicalItemId)}"; ${preview && preview.ok ? preview.plan.closures.length : 0} item(s) closed pointing at it.`,
    });
    onResolved?.({ outcome, canonicalItemId: res.canonicalItemId });
    onClose();
  };

  if (!open || !group) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Resolve cluster"
      label={group.title ?? 'Cluster'}
      description="Decide once what these items are to each other. Nothing collapses silently."
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={busy || !rationale.trim() || !(preview && preview.ok)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitMerge className="h-3.5 w-3.5" />}
            Resolve
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-[var(--text-sec)] mb-1">Outcome</legend>
          {OUTCOMES.map((o) => (
            <label key={o} className={`flex items-start gap-3 border px-3 py-2 cursor-pointer ${outcome === o ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] hover:bg-[var(--surface)]'}`}>
              <input
                type="radio"
                name="cluster-outcome"
                value={o}
                checked={outcome === o}
                onChange={() => setOutcome(o)}
                className="mt-1"
                disabled={busy}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-[var(--text-pri)]">{OUTCOME_LABELS[o].label}</span>
                <span className="block text-xs text-[var(--text-ter)]">{OUTCOME_LABELS[o].summary}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {needsCanonical && (
          <div>
            <label htmlFor="resolve-cluster-canonical" className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Item to keep <span className="text-[var(--coral)]">*</span>
            </label>
            <Select
              id="resolve-cluster-canonical"
              value={canonicalItemId}
              onChange={(e) => setCanonicalItemId(e.target.value)}
              className="w-full"
              disabled={busy}
            >
              {memberItems.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.title}
                </option>
              ))}
            </Select>
          </div>
        )}

        <div>
          <label htmlFor="resolve-cluster-rationale" className="block text-sm font-medium text-[var(--text-sec)] mb-1">
            Why <span className="text-[var(--coral)]">*</span>
          </label>
          <TextArea
            id="resolve-cluster-rationale"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={3}
            placeholder={outcome === 'variant' ? 'What differs between them that is worth keeping?' : 'Why is the kept item the right one?'}
            className="w-full"
            disabled={busy}
          />
        </div>

        <div className="text-xs text-[var(--text-sec)] border border-[var(--rule)] bg-[var(--surface)] px-3 py-2">
          {preview && preview.ok ? (
            preview.plan.closures.length === 0 ? (
              <span>All {memberItems.length} items stay open and individually rankable. The cluster is marked resolved as variants.</span>
            ) : (
              <span>
                Keeps <strong>{titleOf(preview.plan.canonicalItemId ?? '')}</strong>. Closes{' '}
                {preview.plan.closures.map((c) => `"${titleOf(c.itemId)}"`).join(', ')} pointing at it
                {outcome === 'duplicate' ? ', with a lineage record' : ', without a lineage record'}. Closed items leave pool pickers.
                {outcome === 'reframe' ? ' You will be taken to the kept item to reword it.' : ''}
              </span>
            )
          ) : (
            <span className="text-[var(--coral)]">{preview && !preview.ok ? preview.reason : ''}</span>
          )}
        </div>

        {error && <p className="text-sm text-[var(--coral)]">{error}</p>}
      </div>
    </Modal>
  );
}
